import { absoluteGradingEnabled, blendBudgetPenalty } from "../../../../helpers/scheduleGrading";
import React from "react";

const SCORE_COLORS = {
  empty: { fill: "#94a3b8", text: "text-slate-500", bg: "bg-slate-100" },
  great: { fill: "#0f766e", text: "text-teal-700", bg: "bg-teal-50" },
  good: { fill: "#d97706", text: "text-amber-700", bg: "bg-amber-50" },
  fair: { fill: "#2563eb", text: "text-blue-700", bg: "bg-blue-50" },
  poor: { fill: "#dc2626", text: "text-rose-700", bg: "bg-rose-50" },
};

const DEFAULT_GRADE_WEIGHTS = {
  grade_intraday_weight: 0.25,
  grade_budget_weight: 0.75,
  grade_budget_under_multiplier: 0.25,
  grade_budget_over_multiplier: 1,
  grade_budget_difficulty_multiplier: 1,
  grade_intraday_difficulty_multiplier: 1,
};

const GRADE_SCORE_EXPLANATION =
  "Overall grade blends intraday coverage and budget fit using the configured grade weights, so the two scores can influence the final grade differently.";

export const BUSINESS_SCORECARD_METRICS = [
  {
    key: "coverage_score",
    label: "Coverage",
    explanation: "100 minus understaffed hours as a share of target hours.",
  },
  {
    key: "budget_score",
    label: "Budget Fit",
    explanation: "Budget fit uses company percentage and absolute-hours weights, daily allowance, and under/over penalties.",
  },
  {
    key: "minimum_staffing_coverage_percent",
    label: "Min Staffing",
    explanation: "Share of active labor periods at or above the staffing floor.",
  },
  {
    key: "keyholder_coverage_percent",
    label: "Keyholder",
    explanation: "Share of scheduled active shift time covered by a keyholder role.",
  },
  {
    key: "open_shift_score",
    label: "Open Shifts",
    explanation: "100 minus open shift hours as a share of scheduled shift hours.",
  },
  {
    key: "role_distribution_match_percent",
    label: "Role Mix",
    explanation: "Share of periods whose role mix matches recent historical patterns.",
  },
  {
    key: "opening_closing_consistency_score",
    label: "Open/Close",
    explanation: "Share of opener and closer assignments matching recent history.",
  },
];

const BUSINESS_SCORECARD_METRICS_BY_KEY = BUSINESS_SCORECARD_METRICS.reduce(
  (acc, metric) => {
    acc[metric.key] = metric;
    return acc;
  },
  {}
);

function asNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function clampScore(value) {
  const parsed = asNumber(value);
  if (parsed === null) return null;
  return Math.max(0, Math.min(100, parsed));
}

function sumColumn(rows, column) {
  return (rows || []).reduce((total, row) => total + (asNumber(row?.[column]) || 0), 0);
}

function formatScore(value) {
  const score = clampScore(value);
  return score === null ? "-" : `${Math.round(score)}`;
}

function formatScoreWithUnit(value) {
  const score = clampScore(value);
  return score === null ? "-" : `${Math.round(score)}/100`;
}

function formatHours(value) {
  const parsed = asNumber(value);
  return parsed === null ? "-" : `${parsed.toFixed(1)} hrs`;
}

function getScoreTone(score) {
  const normalized = clampScore(score);
  if (normalized === null) return SCORE_COLORS.empty;
  if (normalized >= 80) return SCORE_COLORS.great;
  if (normalized >= 60) return SCORE_COLORS.good;
  if (normalized >= 40) return SCORE_COLORS.fair;
  return SCORE_COLORS.poor;
}

function aggregateLaborByPeriod(rows) {
  const byPeriod = {};
  (rows || []).forEach((row) => {
    const date = row?.date || row?.date_start;
    const qhNum = asNumber(row?.qh_num);
    if (!date || qhNum === null) return;
    const key = `${date}_${qhNum}`;
    const labor = asNumber(row?.labor);
    const hours = asNumber(row?.hours);
    byPeriod[key] =
      (byPeriod[key] || 0) + (labor !== null ? labor : (hours || 0) * 4);
  });
  return byPeriod;
}

function getGradeWeights(gradeWeights) {
  const rawWeights = Array.isArray(gradeWeights) ? gradeWeights[0] : gradeWeights;
  return { ...DEFAULT_GRADE_WEIGHTS, ...(rawWeights || {}) };
}

function averageScore(scores) {
  const validScores = scores.filter(
    (score) => score !== null && score !== undefined
  );
  if (!validScores.length) return null;
  return validScores.reduce((total, score) => total + score, 0) / validScores.length;
}

export function buildGradeAlignedLaborScores({
  laborRows,
  targetLaborRows,
  gradeWeights,
  minimumTargetHours = 0,
}) {
  const weights = getGradeWeights(gradeWeights);
  const dates = [
    ...new Set(
      (targetLaborRows || [])
        .map((row) => row?.date || row?.date_start)
        .filter(Boolean)
    ),
  ];
  const coverageScores = [];
  const budgetScores = [];
  const gradeScores = [];

  dates.forEach((date) => {
    const targetRows = (targetLaborRows || []).filter(
      (row) => (row?.date || row?.date_start) === date
    );
    const actualRows = (laborRows || []).filter(
      (row) => (row?.date || row?.date_start) === date
    );
    const targetHours = sumColumn(targetRows, "hours");
    const actualHours = sumColumn(actualRows, "hours");
    const minimum = absoluteGradingEnabled(weights) ? 10 : minimumTargetHours;
    if (!targetHours || targetHours < minimum) return;
    if (!actualHours) {
      coverageScores.push(0);
      budgetScores.push(0);
      gradeScores.push(0);
      return;
    }

    const targetByPeriod = aggregateLaborByPeriod(targetRows);
    const actualByPeriod = aggregateLaborByPeriod(actualRows);
    const targetLaborSum = Object.values(targetByPeriod).reduce(
      (total, labor) => total + labor,
      0
    );
    let coverageScore = 0;
    let budgetScore = 0;
    if (targetLaborSum > 0) {
      const understaffedLabor = Object.keys(targetByPeriod).reduce((total, key) => {
        const diff = (actualByPeriod[key] || 0) - (targetByPeriod[key] || 0);
        return diff < 0 ? total + diff : total;
      }, 0);
      coverageScore =
        ((understaffedLabor * weights.grade_intraday_difficulty_multiplier +
          targetLaborSum) /
          targetLaborSum) *
        100;
    }

    if (actualHours > 0) {
      const budgetMultiplier =
        targetHours > actualHours
          ? weights.grade_budget_under_multiplier
          : weights.grade_budget_over_multiplier;
      budgetScore =
        (1 -
          blendBudgetPenalty((weights.grade_budget_difficulty_multiplier *
            budgetMultiplier *
            Math.abs(targetHours - actualHours)) /
            targetHours, actualHours, targetHours, weights)) *
        100;
    }

    const intradayWeight =
      targetLaborSum === 0 ? 0 : weights.grade_intraday_weight;
    const budgetWeight =
      targetLaborSum === 0 ? 1 : weights.grade_budget_weight;
    coverageScores.push(coverageScore);
    budgetScores.push(budgetScore);
    gradeScores.push(
      coverageScore * intradayWeight + budgetScore * budgetWeight
    );
  });

  return {
    coverageScore: averageScore(coverageScores),
    budgetScore: averageScore(budgetScores),
    gradeScore: averageScore(gradeScores),
  };
}

export function calculateLaborGradeScore({
  laborRows,
  targetLaborRows,
  gradeWeights,
  minimumTargetHours = 0,
}) {
  return buildGradeAlignedLaborScores({
    laborRows,
    targetLaborRows,
    gradeWeights,
    minimumTargetHours,
  }).gradeScore;
}

function calculateCoverageScore(laborRows, targetLaborRows, targetHours) {
  const targetByPeriod = aggregateLaborByPeriod(targetLaborRows);
  const actualByPeriod = aggregateLaborByPeriod(laborRows);
  const targetKeys = Object.keys(targetByPeriod);
  if (!targetKeys.length || !targetHours || targetHours <= 0) return null;

  const understaffedLabor = targetKeys.reduce((total, key) => {
    const targetLabor = targetByPeriod[key] || 0;
    const actualLabor = actualByPeriod[key] || 0;
    return total + Math.max(targetLabor - actualLabor, 0);
  }, 0);
  const understaffedHours = understaffedLabor / 4;
  return Math.max(0, 100 - Math.min((understaffedHours / targetHours) * 100, 100));
}

function calculateBudgetScore(actualHours, targetHours) {
  if (!targetHours || targetHours <= 0) return null;
  return Math.max(0, 100 - Math.min((Math.abs(actualHours - targetHours) / targetHours) * 100, 100));
}

function calculateOpenShiftScore(scheduleRows, openShiftRows) {
  if (!Array.isArray(scheduleRows) || !scheduleRows.length) return null;
  const scheduledShiftHours = sumColumn(scheduleRows, "hours");
  const openShiftHours = Array.isArray(openShiftRows)
    ? sumColumn(openShiftRows, "hours")
    : (scheduleRows || []).reduce((total, row) => {
        const isOpen =
          row?.store_team_member_id === null ||
          row?.store_team_member_id === undefined ||
          row?.store_team_member_id === "";
        return total + (isOpen ? asNumber(row?.hours) || 0 : 0);
      }, 0);
  if (!scheduledShiftHours || scheduledShiftHours <= 0) return null;
  return Math.max(0, 100 - Math.min((openShiftHours / scheduledShiftHours) * 100, 100));
}

function makeMetric(key, score, valueLabel) {
  const definition = BUSINESS_SCORECARD_METRICS_BY_KEY[key];
  if (!definition || score === null || score === undefined) return null;
  return {
    ...definition,
    score: clampScore(score),
    valueLabel,
  };
}

export function buildLaborBusinessScorecardMetrics({
  laborRows,
  targetLaborRows,
  scheduleRows,
  openShiftRows,
  gradeWeights,
  minimumTargetHours = 0,
}) {
  const actualHours = sumColumn(laborRows, "hours");
  const targetHours = sumColumn(targetLaborRows, "hours");
  const gradeAlignedScores = gradeWeights
    ? buildGradeAlignedLaborScores({
        laborRows,
        targetLaborRows,
        gradeWeights,
        minimumTargetHours,
      })
    : {};
  const metrics = [
    makeMetric(
      "coverage_score",
      gradeAlignedScores.coverageScore ??
        calculateCoverageScore(laborRows, targetLaborRows, targetHours),
      `${formatHours(actualHours)} / ${formatHours(targetHours)}`
    ),
    makeMetric(
      "budget_score",
      gradeAlignedScores.budgetScore ?? calculateBudgetScore(actualHours, targetHours),
      `${formatHours(actualHours)} / ${formatHours(targetHours)}`
    ),
    makeMetric(
      "open_shift_score",
      calculateOpenShiftScore(scheduleRows, openShiftRows),
      openShiftRows?.length ? `${formatHours(sumColumn(openShiftRows, "hours"))} open` : "No open shifts"
    ),
  ];
  return metrics.filter(Boolean);
}

export function buildComparisonBusinessScorecardMetrics({
  currentMetrics,
  proposedMetrics,
  hasKeyholderRoles,
  metricVisibility,
}) {
  const keys = [
    "coverage_score",
    "budget_score",
    "minimum_staffing_coverage_percent",
    ...(hasKeyholderRoles ? ["keyholder_coverage_percent"] : []),
    "open_shift_score",
    "role_distribution_match_percent",
    "opening_closing_consistency_score",
  ];

  return keys
    .filter((key) => {
      if (!Object.prototype.hasOwnProperty.call(metricVisibility || {}, key)) return true;
      return Boolean(metricVisibility[key]);
    })
    .map((key) => {
      const definition = BUSINESS_SCORECARD_METRICS_BY_KEY[key];
      const currentScore = clampScore(currentMetrics?.[key]);
      const proposedScore = clampScore(proposedMetrics?.[key]);
      if (!definition || (currentScore === null && proposedScore === null)) return null;
      return {
        ...definition,
        currentScore,
        proposedScore,
        delta:
          currentScore === null || proposedScore === null
            ? null
            : proposedScore - currentScore,
      };
    })
    .filter(Boolean);
}

export function buildScorecardMetricComparison({
  currentMetrics,
  proposedMetrics,
}) {
  const currentByKey = (currentMetrics || []).reduce((acc, metric) => {
    acc[metric.key] = metric;
    return acc;
  }, {});
  const proposedByKey = (proposedMetrics || []).reduce((acc, metric) => {
    acc[metric.key] = metric;
    return acc;
  }, {});

  return BUSINESS_SCORECARD_METRICS.map((definition) => {
    const currentMetric = currentByKey[definition.key];
    const proposedMetric = proposedByKey[definition.key];
    const currentScore = clampScore(currentMetric?.score);
    const proposedScore = clampScore(proposedMetric?.score);
    if (currentScore === null && proposedScore === null) return null;
    return {
      ...definition,
      currentScore,
      proposedScore,
      currentValueLabel: currentMetric?.valueLabel,
      proposedValueLabel: proposedMetric?.valueLabel,
      delta:
        currentScore === null || proposedScore === null
          ? null
          : proposedScore - currentScore,
    };
  }).filter(Boolean);
}

function ScoreBar({ score }) {
  const normalized = clampScore(score) || 0;
  const tone = getScoreTone(score);
  return (
    <div className="relative h-2 rounded-full bg-slate-200">
      <div
        className="absolute left-0 top-0 h-2 rounded-full"
        style={{ width: `${normalized}%`, backgroundColor: tone.fill }}
      />
      <div className="absolute left-1/2 top-[-3px] h-3.5 w-px bg-white/80" />
    </div>
  );
}

function formatDelta(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  const parsed = Number(value);
  const prefix = parsed > 0 ? "+" : "";
  return `${prefix}${Math.round(parsed)}`;
}

function ComparisonScoreBar({ currentScore, proposedScore }) {
  const current = clampScore(currentScore);
  const proposed = clampScore(proposedScore);
  const base = current ?? proposed ?? 0;
  const next = proposed ?? current ?? 0;
  const baseTone = getScoreTone(base);
  const nextTone = getScoreTone(next);
  const improved = proposed !== null && current !== null && proposed > current;
  const regressed = proposed !== null && current !== null && proposed < current;
  const changeStart = Math.min(base, next);
  const changeWidth = Math.abs(next - base);
  const markerLeft = Math.max(2, Math.min(98, next));

  return (
    <div className="relative h-4">
      <div className="absolute left-0 right-0 top-1/2 h-3 -translate-y-1/2 overflow-hidden rounded-full bg-slate-200">
        <div
          className="absolute left-0 top-0 h-full rounded-full"
          style={{ width: `${base}%`, backgroundColor: baseTone.fill }}
        />
        {changeWidth > 0 && (
          <div
            className={`absolute top-0 h-full rounded-full ${
              improved ? "bg-emerald-400/80" : "bg-rose-400/70"
            }`}
            style={{
              left: `${changeStart}%`,
              width: `${changeWidth}%`,
            }}
          />
        )}
        {regressed && (
          <div
            className="absolute top-0 h-full rounded-full bg-white/35"
            style={{
              left: `${next}%`,
              width: `${changeWidth}%`,
            }}
          />
        )}
        <div className="absolute left-1/2 top-[-3px] h-4 w-px bg-white/80" />
      </div>
      <div
        className="absolute top-1/2 h-4 w-4 rounded-full border-2 border-white shadow-sm"
        style={{
          left: `${markerLeft}%`,
          transform: "translate(-50%, -50%)",
          backgroundColor: nextTone.fill,
        }}
      />
    </div>
  );
}

function DeltaBadge({ value }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  const parsed = Number(value);
  const tone =
    parsed > 0
      ? "bg-emerald-50 text-emerald-700"
      : parsed < 0
      ? "bg-rose-50 text-rose-700"
      : "bg-slate-100 text-slate-500";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {formatDelta(parsed)}
    </span>
  );
}

export default function ScheduleBusinessScorecard({
  title = "Scorecard",
  subtitle,
  metrics = [],
  grade,
  comparison = false,
  showValueLabels = true,
  metricLabelOverrides = {},
  compact = false,
  onMetricClick,
  emptyMessage = "No scorecard metrics available.",
}) {
  if (!metrics.length) {
    return (
      <div className="text-sm text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-slate-900 font-bold text-sm">{title}</h4>
            {subtitle && <div className="mt-1 text-xs text-slate-500">{subtitle}</div>}
          </div>
          {grade && (
            <div className="shrink-0 text-right" title={GRADE_SCORE_EXPLANATION}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Grade
              </div>
              <div className="mt-1 flex items-center justify-end gap-2">
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white"
                  style={{ backgroundColor: grade.color }}
                >
                  {grade.letter}
                </span>
                <div className="text-lg font-bold leading-none text-slate-900">
                  {formatScore(grade.score)}
                  <span className="text-xs font-semibold text-slate-400">/100</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {comparison ? (
        <div className={compact ? "mt-3 space-y-2.5" : "mt-4 space-y-3"}>
          {metrics.map((metric) => {
            const finalScore =
              clampScore(metric.proposedScore) ?? clampScore(metric.currentScore);
            const tone = getScoreTone(finalScore);
            const metricLabel = metricLabelOverrides[metric.key] || metric.label;
            return (
              <div
                key={metric.key}
                className={`rounded-lg border border-slate-100 bg-slate-50/60 p-3 ${
                  onMetricClick ? "cursor-pointer hover:bg-slate-50" : ""
                }`}
                onClick={() => onMetricClick?.(metric)}
                title={metric.explanation}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">
                      {metricLabel}
                    </div>
                    {showValueLabels && (metric.currentValueLabel || metric.proposedValueLabel) && (
                      <div className="mt-0.5 truncate text-[11px] text-slate-400">
                        {metric.proposedValueLabel || metric.currentValueLabel}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <DeltaBadge value={metric.delta} />
                    <div className={`text-sm font-semibold ${tone.text}`}>
                      {formatScore(finalScore)}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <ComparisonScoreBar
                    currentScore={metric.currentScore}
                    proposedScore={metric.proposedScore}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={compact ? "mt-3 space-y-2.5" : "mt-4 space-y-3"}>
          {metrics.map((metric) => {
            const tone = getScoreTone(metric.score);
            const metricLabel = metricLabelOverrides[metric.key] || metric.label;
            return (
              <div key={metric.key} title={metric.explanation}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{metricLabel}</div>
                    {showValueLabels && metric.valueLabel && (
                      <div className="text-[11px] text-slate-400">{metric.valueLabel}</div>
                    )}
                  </div>
                  <div className={`text-sm font-semibold ${tone.text}`}>
                    {formatScoreWithUnit(metric.score)}
                  </div>
                </div>
                <div className={compact ? "mt-1" : "mt-1.5"}>
                  <ScoreBar score={metric.score} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
